"use client";

import type { ShowcasePost } from "@/lib/socialWall";

/* The showcase grid — each tile is a real product's reel, hover-to-play, linking to the
 * buyable product page. preload="none" + play-on-hover keeps it weightless (SEO/CWV). */

const BADGE: Record<string, string> = {
  facebook: "Facebook",
  instagram: "Instagram",
  tiktok: "TikTok",
  youtube: "YouTube",
  pinterest: "Pinterest",
};

export function SocialWallClient({ posts }: { posts: ShowcasePost[] }) {
  return (
    <section className="mt-12">
      <h2 className="text-2xl font-bold tracking-tight">Don&rsquo;t take our word for it — watch them work</h2>
      <p className="mt-1 text-gray-600">Every post below is a real product you can buy. Tap any one.</p>
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {posts.map((p) => {
          const badge = p.platform ? BADGE[p.platform] : undefined;
          return (
            <a
              key={p.id}
              href={p.product_url}
              aria-label={`Buy ${p.product_name}`}
              className="group relative block aspect-[9/16] overflow-hidden rounded-xl bg-gray-100"
            >
              {p.mp4 ? (
                <video
                  src={p.mp4}
                  poster={p.thumb ?? undefined}
                  muted
                  loop
                  playsInline
                  preload="none"
                  onMouseEnter={(e) => void e.currentTarget.play().catch(() => {})}
                  onMouseLeave={(e) => e.currentTarget.pause()}
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              ) : p.thumb ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={p.thumb} alt={p.product_name} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
              ) : (
                <div className="flex h-full w-full items-center justify-center px-2 text-center text-sm text-gray-500">{p.product_name}</div>
              )}
              {badge && (
                <span className="absolute left-2 top-2 rounded-full bg-black/70 px-2 py-0.5 text-xs font-medium text-white backdrop-blur">{badge}</span>
              )}
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-sm font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                Buy this →
              </span>
            </a>
          );
        })}
      </div>
    </section>
  );
}
